import { ArrowDown, Copy, Pause, Play, RefreshCw, Search, Server } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { loadSystemConsole, type ConsoleLogLevel, type SystemConsoleSnapshot } from './logs-api';
import './logs.css';

function bytes(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '—';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let size = Math.max(0, value);
  let unit = 0;
  while (size >= 1024 && unit < units.length - 1) { size /= 1024; unit += 1; }
  return `${size >= 10 || unit === 0 ? size.toFixed(0) : size.toFixed(1)} ${units[unit]}`;
}

function duration(seconds: number): string {
  const total = Math.max(0, Math.floor(seconds));
  const days = Math.floor(total / 86400);
  const hours = Math.floor((total % 86400) / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  if (days) return `${days}d ${hours}h`;
  if (hours) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
}

export function LogsPage() {
  const [snapshot, setSnapshot] = useState<SystemConsoleSnapshot | null>(null);
  const [loading, setLoading] = useState(true);
  const [paused, setPaused] = useState(false);
  const [autoScroll, setAutoScroll] = useState(true);
  const logRef = useRef<HTMLDivElement | null>(null);
  const [query, setQuery] = useState('');
  const [level, setLevel] = useState<'all' | ConsoleLogLevel>('all');
  const [source, setSource] = useState('all');
  const [message, setMessage] = useState('');

  const refresh = useCallback(async () => {
    try {
      setSnapshot(await loadSystemConsole());
      setMessage('');
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Logs could not be loaded.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
    if (paused) return;
    const timer = window.setInterval(() => void refresh(), 3000);
    return () => window.clearInterval(timer);
  }, [paused, refresh]);

  const sources = useMemo(() => Array.from(new Set((snapshot?.logs ?? []).map((entry) => entry.source))).sort((a, b) => a.localeCompare(b, 'en-GB')), [snapshot]);
  const visibleLogs = useMemo(() => {
    const search = query.trim().toLocaleLowerCase('en-GB');
    return (snapshot?.logs ?? []).filter((entry) => {
      if (level !== 'all' && entry.level !== level) return false;
      if (source !== 'all' && entry.source !== source) return false;
      return !search || `${entry.source} ${entry.message}`.toLocaleLowerCase('en-GB').includes(search);
    });
  }, [level, query, snapshot, source]);

  useEffect(() => {
    if (!autoScroll || paused) return;
    const frame = window.requestAnimationFrame(() => {
      const node = logRef.current;
      if (node) node.scrollTop = node.scrollHeight;
    });
    return () => window.cancelAnimationFrame(frame);
  }, [autoScroll, paused, visibleLogs]);

  async function copyVisible() {
    const text = visibleLogs.map((entry) => `${entry.timestamp} [${entry.level.toUpperCase()}] ${entry.source}: ${entry.message}`).join('\n');
    try {
      await navigator.clipboard.writeText(text);
      setMessage(`Copied ${visibleLogs.length.toLocaleString('en-GB')} visible log entries.`);
    } catch {
      setMessage('The browser did not allow the log copy operation.');
    }
  }

  if (loading && !snapshot) return <div className="ffs-next-loading">Loading Logs…</div>;

  const memoryPercent = snapshot?.host.memory.totalBytes ? Math.round((snapshot.host.memory.usedBytes / snapshot.host.memory.totalBytes) * 100) : 0;
  const diskPercent = snapshot?.host.disk?.totalBytes ? Math.round((snapshot.host.disk.usedBytes / snapshot.host.disk.totalBytes) * 100) : 0;

  return (
    <div className="ffs-workspace-page ffs-logs-page">
      {message ? <div className="ffs-workspace-message">{message}</div> : null}
      <section className="ffs-workspace-hero">
        <div><small>DIAGNOSTICS</small><h2>Logs</h2><p>Search the bounded browser-safe server log without mixing it into everyday system status.</p></div>
        <div className="ffs-logs-hero-actions"><span className={`ffs-status ${paused ? 'warning' : 'good'}`}>{paused ? 'PAUSED' : 'LIVE'}</span><button type="button" className="ffs-button secondary" onClick={() => setPaused((value) => !value)}>{paused ? <Play size={14} /> : <Pause size={14} />} {paused ? 'Resume' : 'Pause'}</button><button type="button" className={`ffs-button secondary ${autoScroll ? 'active' : ''}`} aria-pressed={autoScroll} onClick={() => setAutoScroll((value) => !value)}><ArrowDown size={14} /> Auto-scroll {autoScroll ? 'On' : 'Off'}</button><button type="button" className="ffs-button secondary" onClick={() => void refresh()}><RefreshCw size={14} /> Refresh</button></div>
      </section>

      <section className="ffs-logs-runtime-grid">
        <Runtime label="Host" value={snapshot?.host.hostname ?? '—'} detail={`Up ${duration(snapshot?.host.uptimeSeconds ?? 0)}`} />
        <Runtime label="Memory" value={`${memoryPercent}%`} detail={`${bytes(snapshot?.host.memory.usedBytes)} / ${bytes(snapshot?.host.memory.totalBytes)}`} />
        <Runtime label="Disk" value={snapshot?.host.disk ? `${diskPercent}%` : '—'} detail={snapshot?.host.disk ? `${bytes(snapshot.host.disk.freeBytes)} free` : 'Filesystem stats unavailable'} />
        <Runtime label="Server process" value={duration(snapshot?.runtime.processUptimeSeconds ?? 0)} detail={`RSS ${bytes(snapshot?.runtime.rssBytes)} · Heap ${bytes(snapshot?.runtime.heapUsedBytes)}`} />
        <Runtime label="Container" value={snapshot?.runtime.container ?? 'unknown'} detail={`Proxy ${snapshot?.runtime.reverseProxy ?? 'unknown'}`} />
        <Runtime label="Load" value={(snapshot?.host.loadAverage?.[0] ?? 0).toFixed(2)} detail={(snapshot?.host.loadAverage ?? []).map((item) => item.toFixed(2)).join(' / ')} />
      </section>

      <section className="ffs-workspace-card ffs-logs-card">
        <header><div><small>SERVER LOG</small><h3>Recent entries</h3></div><span>{visibleLogs.length.toLocaleString('en-GB')} shown</span></header>
        <div className="ffs-logs-toolbar">
          <label className="search"><Search size={15} /><input type="search" value={query} placeholder="Search source or message" onChange={(event) => setQuery(event.target.value)} /></label>
          <label><span>Level</span><select value={level} onChange={(event) => setLevel(event.target.value as typeof level)}><option value="all">All levels</option><option value="error">Errors</option><option value="warn">Warnings</option><option value="info">Info</option></select></label>
          <label><span>Source</span><select value={source} onChange={(event) => setSource(event.target.value)}><option value="all">All sources</option>{sources.map((item) => <option value={item} key={item}>{item}</option>)}</select></label>
          <button type="button" className="ffs-button secondary" disabled={visibleLogs.length === 0} onClick={() => void copyVisible()}><Copy size={14} /> Copy Visible</button>
        </div>
        <div className="ffs-log-table" ref={logRef} role="log" aria-live={paused ? 'off' : 'polite'}>
          {visibleLogs.length === 0 ? <div className="ffs-empty-state">No log entries match the current filters.</div> : visibleLogs.map((entry, index) => <article className={`level-${entry.level}`} key={`${entry.timestamp}-${index}`}><time>{new Date(entry.timestamp).toLocaleTimeString('en-GB')}</time><span className="level">{entry.level.toUpperCase()}</span><strong>{entry.source}</strong><p>{entry.message}</p></article>)}
        </div>
        <div className="ffs-logs-footer"><Server size={13} /><span>Browser view is bounded to recent entries. Operator sessions receive server-side redaction of sensitive-looking values.</span><small>Checked {snapshot?.checkedAt ? new Date(snapshot.checkedAt).toLocaleTimeString('en-GB') : '—'}</small></div>
      </section>
    </div>
  );
}

function Runtime({ label, value, detail }: { label: string; value: string; detail: string }) {
  return <article><span>{label}</span><strong>{value}</strong><small>{detail}</small></article>;
}
